import app from '../../firebase';
import { isDev } from 'config';

export type ReportFile = { orgId: string; reportId: string; thumbnail?: boolean };

export async function fetchReportFile(file: ReportFile, signal?: AbortSignal): Promise<Blob> {
  const user = app.auth().currentUser;
  if (!user) throw new Error('Log ind igen.');
  const project = (app.options as { projectId?: string }).projectId;
  if (!project) throw new Error('Fillageret er ikke konfigureret.');
  const endpoint = isDev
    ? `http://127.0.0.1:5001/${project}/europe-west3/reportFile`
    : `https://europe-west3-${project}.cloudfunctions.net/reportFile`;
  const query = new URLSearchParams({ orgId: file.orgId, reportId: file.reportId });
  if (file.thumbnail) query.set('thumbnail', 'true');
  // ponytail: like the existing downloader, this buffers the file in browser
  // memory; use a streaming file-save API if reports outgrow device memory.
  const parts: Blob[] = [];
  let offset = 0;
  let etag: string | null = null;
  do {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${await user.getIdToken()}`,
      Range: `bytes=${offset}-`,
    };
    if (etag) headers['If-Match'] = etag;
    const response = await fetch(`${endpoint}?${query}`, {
      headers, signal, credentials: 'omit', cache: 'no-store', redirect: 'error',
    });
    if (!response.ok) throw new Error('Rapporten kunne ikke hentes. Kontrollér din adgang og prøv igen.');
    if (offset === 0 && response.headers.get('Content-Range') === 'bytes */0') {
      const empty = await response.blob();
      if (empty.size || app.auth().currentUser?.uid !== user.uid) throw new Error('Download blev afbrudt.');
      return empty;
    }
    const range = /^bytes (\d+)-(\d+)\/(\d+)$/.exec(response.headers.get('Content-Range') || '');
    const nextTag = response.headers.get('ETag');
    if (!range || Number(range[1]) !== offset || !nextTag || (etag && etag !== nextTag))
      throw new Error('Filen er ændret. Prøv igen.');
    const chunk = await response.blob();
    if (chunk.size !== Number(range[2]) - offset + 1 || !chunk.size)
      throw new Error('Download blev afbrudt. Prøv igen.');
    if (app.auth().currentUser?.uid !== user.uid) throw new Error('Login er ændret. Log ind igen.');
    parts.push(chunk);
    etag = nextTag;
    offset += chunk.size;
    if (offset === Number(range[3])) return new Blob(parts);
    if (offset > Number(range[3])) throw new Error('Ugyldig filstørrelse.');
  } while (true);
}
